import { CommonModule } from '@angular/common'
import type { OnInit } from '@angular/core'
import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Events } from '@ngrx/signals/events'

import { ConversationStore } from '@app/modules/messaging/components/store/conversation.store'
import { sendingEvents } from '@app/modules/messaging/components/store/sending.events'
import { Closeable } from '@app/overlays/closeable'

import { VoiceMessageCreatorExitConfirmComponent } from '../voice-message-creator-exit-confirm/voice-message-creator-exit-confirm.component'
import { VoiceMessageCreatorMicAccessRequiredComponent } from '../voice-message-creator-mic-access-required/voice-message-creator-mic-access-required.component'
import { VoiceMessageRecorderComponent } from '../voice-message-recorder/voice-message-recorder.component'
import { VoiceMessageRecorderStore } from '../voice-message-recorder/voice-message-recorder.store'

@UntilDestroy()
@Component({
  selector: 'sb-voice-message-creator-modal',
  templateUrl: './voice-message-creator-modal.component.html',
  imports: [
    CommonModule,
    VoiceMessageRecorderComponent,
    VoiceMessageCreatorMicAccessRequiredComponent,
    VoiceMessageCreatorExitConfirmComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VoiceMessageRecorderStore],
})
export class VoiceMessageCreatorModalComponent extends Closeable implements OnInit {
  private readonly events = inject(Events)
  private readonly conversationStore = inject(ConversationStore)
  readonly voiceMessageRecorderStore = inject(VoiceMessageRecorderStore)

  readonly showExitConfirmation = signal<boolean>(false)
  readonly duration = signal<number | undefined>(undefined)

  private readonly voiceMessageRecorderComponent = viewChild(VoiceMessageRecorderComponent)

  ngOnInit(): void {
    this.events
      .on(sendingEvents.sendMessageSuccess)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.close()
      })
  }

  override attemptToClose(type: string | undefined = undefined): boolean {
    if (!type) {
      return true
    }

    if (this.showExitConfirmation()) {
      const reason = this.duration() ? 'exit' : 'cancel'
      this.voiceMessageRecorderStore.exitConfirmationModal(reason)
      return true
    }

    const recordingInProgress = this.voiceMessageRecorderStore.recordingEndsAt()
    const uploadInProgress = this.conversationStore.voiceUploadInProgress()
    const voice = this.conversationStore.voice()

    if (!voice && !recordingInProgress && !uploadInProgress) {
      return true
    }

    if (recordingInProgress) {
      const elapsedMs =
        this.voiceMessageRecorderStore.maxDurationMs() - (new Date(recordingInProgress).getTime() - Date.now())

      this.conversationStore.stopRecording()

      if (elapsedMs < this.voiceMessageRecorderStore.minDurationMs()) {
        return false
      }
    }

    if (voice) {
      this.duration.set(this.voiceMessageRecorderComponent()!.player()!.duration())
    }

    this.showExitConfirmation.set(true)
    return false
  }
}
